import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { studentAPI, subscriptionAPI, adPreferencesAPI, narrativeAPI, classroomManageAPI } from '@/lib/api';
import { BrutalButton, BrutalCard, BrutalBadge, BrutalProgress } from '@/components/brutal';
import { Plus, Edit, Trash2, Copy, Check, BookOpen, RefreshCw, Type, SpellCheck, Megaphone, User, Camera, Sparkles, X, Upload, Download, FileSpreadsheet, Mail, ChevronDown, ChevronUp, Bell, Target, Clock } from 'lucide-react';
import { toast } from 'sonner';
import StudentFormDialog from './StudentFormDialog';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import ParentalControlsPanel from './ParentalControlsPanel';

const StudentsTab = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [deletingStudent, setDeletingStudent] = useState(null);
  const [copiedPin, setCopiedPin] = useState(null);
  const [assigningBanksStudent, setAssigningBanksStudent] = useState(null);
  const [resettingPin, setResettingPin] = useState(null);
  const [changingPin, setChangingPin] = useState(null);
  const [pinForm, setPinForm] = useState({ current_pin: '', new_pin: '', confirm_pin: '' });
  const [storyDialogOpen, setStoryDialogOpen] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [storyPrompt, setStoryPrompt] = useState('');
  const [storyGenerating, setStoryGenerating] = useState(false);
  const [storyProgress, setStoryProgress] = useState('');
  const [personalized, setPersonalized] = useState(true);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [bulkImportFile, setBulkImportFile] = useState(null);
  const [bulkImportLoading, setBulkImportLoading] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState(null);
  const [bulkImportClassroom, setBulkImportClassroom] = useState('');
  const [sendingInvitations, setSendingInvitations] = useState(false);
  const [trackerExpanded, setTrackerExpanded] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  const [filterClassroom, setFilterClassroom] = useState('');
  const [bulkSelectedIds, setBulkSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);

  // Fetch enrollment tracker (teachers only)
  const { data: trackerData } = useQuery({
    queryKey: ['enrollment-tracker', user?.id],
    queryFn: async () => {
      const response = await studentAPI.getEnrollmentTracker();
      return response.data;
    },
    enabled: !!user?.id && !!user?.is_teacher,
  });

  // Fetch students (no explicit guardian_id so backend returns owned + teacher-linked)
  const { data: students = [], isLoading: studentsLoading } = useQuery({
    queryKey: ['students', user?.id],
    queryFn: async () => {
      const response = await studentAPI.getAll();
      return response.data;
    },
    enabled: !!user?.id
  });

  // Fetch classrooms (for bulk import dropdown)
  const { data: classroomsForImport } = useQuery({
    queryKey: ['classrooms-for-import'],
    queryFn: async () => (await classroomManageAPI.getAll()).data,
    enabled: !!user?.is_teacher,
  });

  // Fetch subscription
  const { data: subscription } = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: async () => {
      const response = await subscriptionAPI.get(user?.id);
      return response.data;
    },
    enabled: !!user?.id
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (studentId) => studentAPI.delete(studentId),
    onSuccess: () => {
      queryClient.invalidateQueries(['students']);
      queryClient.invalidateQueries(['subscription']);
      toast.success('Student deleted successfully');
      setDeletingStudent(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to delete student');
    }
  });

  // Reset PIN mutation
  const resetPinMutation = useMutation({
    mutationFn: (studentId) => studentAPI.resetPin(studentId),
    onSuccess: (response, studentId) => {
      queryClient.invalidateQueries(['students']);
      const newPin = response.data.new_pin;
      // Persistent toast — this is the only time this PIN will ever be shown.
      toast.success(`New PIN: ${newPin}  (copied to clipboard — give to parent)`, { duration: 30000 });
      try { navigator.clipboard.writeText(newPin); } catch (_) {}
      setResettingPin(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to reset PIN');
      setResettingPin(null);
    }
  });

  // Spellcheck toggle mutation
  const changePinMutation = useMutation({
    mutationFn: ({ studentId, data }) => studentAPI.changePin(studentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['students']);
      toast.success('PIN changed successfully!');
      setChangingPin(null);
      setPinForm({ current_pin: '', new_pin: '', confirm_pin: '' });
    },
    onError: (error) => toast.error(error.response?.data?.detail || 'Failed to change PIN'),
  });

  const handleChangePin = (studentId) => {
    if (pinForm.new_pin !== pinForm.confirm_pin) {
      toast.error('New PINs do not match');
      return;
    }
    changePinMutation.mutate({ studentId, data: { current_pin: pinForm.current_pin, new_pin: pinForm.new_pin } });
  };

  // Spellcheck toggle mutation (original)
  const spellcheckMutation = useMutation({
    mutationFn: (studentId) => studentAPI.toggleSpellcheck(studentId),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['students'] });
      toast.success(`Spellcheck ${response.data.spellcheck_disabled ? 'disabled' : 'enabled'}`);
    },
  });

  // Spelling mode toggle mutation
  const spellingModeMutation = useMutation({
    mutationFn: (studentId) => studentAPI.toggleSpellingMode(studentId),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['students'] });
      toast.success(`Spelling mode: ${response.data.spelling_mode}`);
    },
  });

  // Ad preferences toggle
  const adPrefMutation = useMutation({
    mutationFn: ({ studentId, prefs }) => adPreferencesAPI.update(studentId, prefs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] });
      toast.success('Brand story preferences updated');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed'),
  });

  const [goalForm, setGoalForm] = useState({ studentId: null, stories: 2, period: 'weekly', start: '', end: '' });

  const setGoalMutation = useMutation({
    mutationFn: ({ id, data }) => studentAPI.setReadingGoal(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] });
      setGoalForm({ ...goalForm, studentId: null });
      toast.success('Reading goal set!');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to set goal'),
  });

  const removeGoalMutation = useMutation({
    mutationFn: (id) => studentAPI.removeReadingGoal(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] });
      toast.success('Reading goal removed');
    },
  });

  const handleEdit = (student) => {
    setEditingStudent(student);
    setIsFormOpen(true);
  };

  const handleAssignBanks = (student) => {
    setEditingStudent(student);
    setAssigningBanksStudent(student);
    setIsFormOpen(true);
  };

  const handleDelete = (student) => {
    setDeletingStudent(student);
  };

  const handleCopyPin = (pin, studentName) => {
    navigator.clipboard.writeText(pin);
    setCopiedPin(pin);
    toast.success(`PIN copied for ${studentName}`);
    setTimeout(() => setCopiedPin(null), 2000);
  };

  const handleAddStudent = () => {
    setEditingStudent(null);
    setIsFormOpen(true);
  };

  const quickIdeas = [
    'An adventure in space',
    'A mystery at the beach',
    'A trip to the rainforest',
    'A day as a superhero',
    'A magical cooking contest',
    'An underwater treasure hunt',
  ];

  const handleOpenStoryDialog = (preselectedStudentId = null) => {
    if (preselectedStudentId) {
      setSelectedStudentIds([preselectedStudentId]);
    } else {
      setSelectedStudentIds([]);
    }
    setStoryPrompt('');
    setStoryProgress('');
    setPersonalized(true);
    setStoryDialogOpen(true);
  };

  const handleToggleStudent = (studentId) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStudentIds.length === students.length) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(students.map((s) => s.id));
    }
  };

  const handleGenerateStories = async () => {
    if (selectedStudentIds.length === 0) {
      toast.error('Please select at least one student');
      return;
    }
    if (!storyPrompt.trim()) {
      toast.error('Please enter a story prompt');
      return;
    }
    setStoryGenerating(true);
    try {
      // Show progress for each student
      for (let i = 0; i < selectedStudentIds.length; i++) {
        const s = students.find((st) => st.id === selectedStudentIds[i]);
        setStoryProgress(`Generating for ${s?.full_name || 'student'}... (${i + 1}/${selectedStudentIds.length})`);
      }
      const response = await narrativeAPI.createBatch({
        student_ids: selectedStudentIds,
        prompt: storyPrompt.trim(),
        bank_ids: [],
        personalized,
      });
      const data = response.data;
      if (data.generated > 0) {
        toast.success(`Stories generated for ${data.generated} student${data.generated > 1 ? 's' : ''}!`);
      }
      if (data.failed > 0) {
        const failedNames = data.results.filter((r) => r.status === 'failed').map((r) => r.student_name).join(', ');
        toast.error(`Failed for: ${failedNames}`);
      }
      setStoryDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Story generation failed');
    } finally {
      setStoryGenerating(false);
      setStoryProgress('');
    }
  };

  const handleBulkImport = async () => {
    if (!bulkImportFile) {
      toast.error('Please select a CSV file');
      return;
    }
    setBulkImportLoading(true);
    setBulkImportResult(null);
    try {
      const response = await studentAPI.bulkImport(bulkImportFile, bulkImportClassroom);
      setBulkImportResult(response.data);
      queryClient.invalidateQueries(['students']);
      queryClient.invalidateQueries(['subscription']);
      if (response.data.created > 0) {
        const classMsg = response.data.classroom_assigned ? ` and added to classroom (${response.data.classroom_assigned})` : '';
        toast.success(`Created ${response.data.created} students${classMsg}!`);
      }
      if (response.data.errors?.length > 0) {
        toast.error(`${response.data.errors.length} rows had errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk import failed');
    } finally {
      setBulkImportLoading(false);
    }
  };

  const pendingParentStudents = students.filter(
    (s) => s.enrollment_status === 'pending_parent_signup' && s.pending_parent_email
  );
  const pendingParentEmails = [...new Set(pendingParentStudents.map((s) => s.pending_parent_email))];

  const handleSendInvitations = async () => {
    setSendingInvitations(true);
    try {
      const response = await studentAPI.sendInvitations();
      const { invitations_sent } = response.data;
      if (invitations_sent > 0) {
        toast.success(`Invitations sent to ${invitations_sent} parent${invitations_sent !== 1 ? 's' : ''}!`);
      } else {
        toast.info('No pending invitations to send');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitations');
    } finally {
      setSendingInvitations(false);
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await studentAPI.sendReminders();
      const { reminders_sent, pending_signups_reminded, incomplete_profiles_reminded } = response.data;
      if (reminders_sent > 0) {
        toast.success(`Reminders sent! ${pending_signups_reminded} pending signup(s), ${incomplete_profiles_reminded} incomplete profile(s)`);
        queryClient.invalidateQueries(['enrollment-tracker']);
      } else {
        toast.info('No reminders needed - all parents are up to date');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const handleBulkToggle = (studentId) => {
    setBulkSelectedIds((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  };

  const handleBulkSelectAll = () => {
    if (bulkSelectedIds.length === filteredStudents.length) {
      setBulkSelectedIds([]);
    } else {
      setBulkSelectedIds(filteredStudents.map((s) => s.id));
    }
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const response = await studentAPI.bulkDelete(bulkSelectedIds);
      toast.success(`Deleted ${response.data.deleted} student(s) and all their data`);
      queryClient.invalidateQueries(['students']);
      queryClient.invalidateQueries(['subscription']);
      setBulkSelectedIds([]);
      setBulkDeleteConfirmOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const canAddMore = user?.is_teacher || (subscription && subscription.active_students < subscription.student_seats);

  // Filter students by search, grade, and classroom
  const filteredStudents = students.filter(s => {
    if (searchQuery && !s.full_name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (filterGrade && s.grade_level !== filterGrade) return false;
    if (filterClassroom && classroomsForImport) {
      const classroom = classroomsForImport.find(c => c.id === filterClassroom);
      if (classroom && !classroom.student_ids?.includes(s.id)) return false;
    }
    return true;
  });

  if (studentsLoading) {
    return <div className="text-center py-12 text-2xl font-bold">Loading students...</div>;
  }

  return (
    <div className="space-y-8">
      {/* Enrollment Tracker (Teachers Only) */}
      {user?.is_teacher && trackerData && (
        <BrutalCard variant="amber" shadow="xl">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-black uppercase">Enrollment Tracker</h2>
            <BrutalButton
              variant="rose"
              size="sm"
              onClick={handleSendReminders}
              disabled={sendingReminders}
              className="flex items-center gap-2"
            >
              <Bell size={18} className={sendingReminders ? 'animate-spin' : ''} />
              {sendingReminders ? 'Sending...' : 'Send Reminders'}
            </BrutalButton>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <p className="font-bold text-sm mb-2">
              {trackerData.active}/{trackerData.total_students} students fully enrolled
            </p>
            <BrutalProgress
              value={trackerData.active}
              max={trackerData.total_students || 1}
              variant="emerald"
              showLabel
              size="lg"
            />
          </div>

          {/* Status Badges */}
          <div className="flex flex-wrap gap-3 mb-4">
            <span className="inline-flex items-center gap-2 px-4 py-2 border-4 border-black font-black text-sm bg-emerald-100 text-emerald-800">
              {trackerData.active} Active
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 border-4 border-black font-black text-sm bg-yellow-100 text-yellow-800">
              {trackerData.pending_parent_signup} Awaiting Parent Signup
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 border-4 border-black font-black text-sm bg-orange-100 text-orange-800">
              {trackerData.incomplete_profiles} Incomplete Profiles
            </span>
          </div>

          {/* Expandable Parent List */}
          {trackerData.parents_by_status && trackerData.parents_by_status.length > 0 && (
            <div>
              <button
                onClick={() => setTrackerExpanded(!trackerExpanded)}
                className="flex items-center gap-2 font-bold text-sm uppercase text-gray-700 hover:text-black mb-2"
              >
                {trackerExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                {trackerExpanded ? 'Hide' : 'Show'} Parent Details ({trackerData.parents_by_status.length})
              </button>

              {trackerExpanded && (
                <div className="space-y-2 mt-2">
                  {trackerData.parents_by_status.map((parent, idx) => (
                    <div
                      key={idx}
                      className="flex flex-wrap items-center gap-3 p-3 border-4 border-black bg-white"
                    >
                      {/* Parent Email */}
                      <div className="flex-1 min-w-[200px]">
                        <p className="font-bold text-sm truncate">{parent.parent_email}</p>
                        <p className="text-xs text-gray-500">
                          Children: {parent.children.join(', ')}
                        </p>
                      </div>

                      {/* Status Badge */}
                      <span
                        className={`inline-block px-3 py-1 text-xs font-black border-2 border-black ${
                          parent.status === 'pending_signup'
                            ? 'bg-yellow-100 text-yellow-800'
                            : parent.status === 'signed_up_incomplete'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-emerald-100 text-emerald-800'
                        }`}
                      >
                        {parent.status === 'pending_signup'
                          ? 'Pending'
                          : parent.status === 'signed_up_incomplete'
                          ? 'Signed Up'
                          : 'Complete'}
                      </span>

                      {/* Profile Completion Bar */}
                      <div className="w-32">
                        <div className="flex items-center justify-between text-xs font-bold mb-1">
                          <span>Profile</span>
                          <span>{parent.profile_completion}%</span>
                        </div>
                        <div className="w-full h-3 bg-gray-200 border-2 border-black">
                          <div
                            className={`h-full transition-all ${
                              parent.profile_completion >= 80
                                ? 'bg-emerald-500'
                                : parent.profile_completion >= 50
                                ? 'bg-yellow-500'
                                : 'bg-rose-500'
                            }`}
                            style={{ width: `${parent.profile_completion}%` }}
                          />
                        </div>
                      </div>

                      {/* Resend Invitation Button (for pending only) */}
                      {parent.status === 'pending_signup' && (
                        <BrutalButton
                          variant="amber"
                          size="sm"
                          onClick={handleSendInvitations}
                          disabled={sendingInvitations}
                          className="flex items-center gap-1 text-xs"
                        >
                          <Mail size={14} />
                          {sendingInvitations ? 'Sending...' : 'Resend'}
                        </BrutalButton>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </BrutalCard>
      )}

      {/* Subscription Info */}
      {subscription && (
        <BrutalCard variant="indigo" shadow="xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-3xl font-black uppercase mb-2">Subscription</h2>
              <p className="text-xl font-bold capitalize">{subscription.plan} Plan</p>
            </div>
            <BrutalBadge variant="indigo" size="lg">
              {subscription.status}
            </BrutalBadge>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="font-bold uppercase text-sm mb-2">Student Seats</p>
              <BrutalProgress
                value={subscription.active_students}
                max={subscription.student_seats}
                variant="indigo"
                showLabel
                size="lg"
              />
            </div>
            <div>
              <p className="font-bold uppercase text-sm mb-2">Word Banks Purchased</p>
              <div className="text-4xl font-black">{subscription.bank_access?.length || 0}</div>
            </div>
          </div>
        </BrutalCard>
      )}

      {/* Add Student Button */}
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-black uppercase">Your Students</h2>
        <div className="flex items-center gap-3">
          {students.length > 0 && (
            <BrutalButton
              variant="indigo"
              size="lg"
              onClick={() => handleOpenStoryDialog()}
              className="flex items-center gap-2"
            >
              <Sparkles size={24} />
              Generate Story for Students
            </BrutalButton>
          )}
          {pendingParentEmails.length > 0 && (
            <BrutalButton
              variant="rose"
              size="lg"
              onClick={handleSendInvitations}
              disabled={sendingInvitations}
              className="flex items-center gap-2"
            >
              <Mail size={24} className={sendingInvitations ? 'animate-spin' : ''} />
              {sendingInvitations
                ? 'Sending...'
                : `Send Parent Invitations (${pendingParentEmails.length})`}
            </BrutalButton>
          )}
          <BrutalButton
            variant="amber"
            size="lg"
            onClick={() => { setBulkImportOpen(true); setBulkImportFile(null); setBulkImportResult(null); }}
            className="flex items-center gap-2"
          >
            <Upload size={24} />
            Bulk Import
          </BrutalButton>
          <BrutalButton
            variant="emerald"
            size="lg"
            onClick={handleAddStudent}
            disabled={!canAddMore}
            className="flex items-center gap-2"
          >
            <Plus size={24} />
            Add Student
          </BrutalButton>
        </div>
      </div>

      {!canAddMore && subscription && (
        <BrutalCard variant="amber">
          <p className="font-bold text-lg">
            ⚠️ You've reached your student seat limit ({subscription.student_seats}). 
            Upgrade your plan to add more students.
          </p>
        </BrutalCard>
      )}

      {/* Search & Filter Bar */}
      {students.length > 5 && (
        <div className="flex flex-wrap gap-3 items-center p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search students by name..."
              className="w-full px-4 py-2.5 rounded-lg text-sm font-medium border-4 border-black bg-white"
              style={{ color: '#111827' }}
            />
          </div>
          <select
            value={filterGrade}
            onChange={(e) => setFilterGrade(e.target.value)}
            className="px-3 py-2.5 rounded-lg text-sm font-bold border-4 border-black bg-white"
            style={{ color: '#111827' }}
          >
            <option value="">All Grades</option>
            <option value="pre-k">Pre-K</option>
            <option value="k">Kindergarten</option>
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={String(i + 1)}>Grade {i + 1}</option>
            ))}
            <option value="college">College</option>
            <option value="adult">Adult</option>
          </select>
          {user?.is_teacher && classroomsForImport && classroomsForImport.length > 0 && (
            <select
              value={filterClassroom}
              onChange={(e) => setFilterClassroom(e.target.value)}
              className="px-3 py-2.5 rounded-lg text-sm font-bold border-4 border-black bg-white"
              style={{ color: '#111827' }}
            >
              <option value="">All Classrooms</option>
              {classroomsForImport.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
          {(searchQuery || filterGrade || filterClassroom) && (
            <button
              onClick={() => { setSearchQuery(''); setFilterGrade(''); setFilterClassroom(''); }}
              className="px-3 py-2.5 rounded-lg text-xs font-bold bg-gray-200 text-gray-700 hover:bg-gray-300"
            >
              Clear Filters
            </button>
          )}
          <span className="text-xs font-bold" style={{ color: '#94A3B8' }}>
            {filteredStudents.length} of {students.length} students
          </span>
        </div>
      )}

      {/* Select All Checkbox */}
      {filteredStudents.length > 0 && (
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer font-bold text-sm">
            <input
              type="checkbox"
              checked={bulkSelectedIds.length === filteredStudents.length && filteredStudents.length > 0}
              onChange={handleBulkSelectAll}
              className="w-5 h-5 accent-indigo-600"
            />
            Select All ({bulkSelectedIds.length}/{filteredStudents.length})
          </label>
        </div>
      )}

      {/* Students Grid */}
      {students.length === 0 ? (
        <BrutalCard shadow="xl" className="text-center py-12">
          <p className="text-2xl font-bold mb-4">No students yet</p>

          <p className="text-lg font-medium mb-6">Add your first student to get started!</p>
          <BrutalButton
            variant="indigo"
            size="lg"
            onClick={handleAddStudent}
            disabled={!canAddMore}
            className="flex items-center gap-2 mx-auto"
          >
            <Plus size={24} />
            Add Your First Student
          </BrutalButton>
        </BrutalCard>
      ) : filteredStudents.length === 0 ? (
        <BrutalCard shadow="md" className="text-center py-8">
          <p className="text-lg font-bold mb-2">No students match your search</p>
          <p className="text-sm text-gray-500">Try a different name, grade, or classroom filter</p>
        </BrutalCard>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredStudents.map((student) => (
            <BrutalCard
              key={student.id}
              shadow="lg"
              hover
              className="relative"
            >
              {/* Bulk Select Checkbox */}
              <div className="absolute top-3 right-3 z-10">
                <input
                  type="checkbox"
                  checked={bulkSelectedIds.includes(student.id)}
                  onChange={() => handleBulkToggle(student.id)}
                  className="w-5 h-5 accent-indigo-600 cursor-pointer"
                />
              </div>

              {/* Student Info */}
              <div className="mb-4">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center border-4 border-indigo-200" style={{ background: '#e8e5ff' }}>
                    {student.photo_url ? (
                      <img src={student.photo_url} alt={student.full_name} className="w-full h-full object-cover" />
                    ) : (
                      <User size={28} className="text-indigo-400" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-2xl font-black uppercase">{student.full_name}</h3>
                    {!student.photo_url && (
                      <label className="inline-flex items-center gap-1 text-[10px] font-bold text-purple-600 cursor-pointer hover:text-purple-800">
                        <Camera size={10} /> Add photo
                        <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            await studentAPI.uploadPhoto(student.id, file);
                            queryClient.invalidateQueries(['students']);
                            toast.success('Photo uploaded!');
                          } catch (err) { toast.error('Upload failed'); }
                          e.target.value = '';
                        }} />
                      </label>
                    )}
                  </div>
                </div>
                
                {/* Student Code & PIN Display */}
                <div className="space-y-2 mb-3">
                  <div>
                    <p className="font-bold text-xs uppercase text-gray-600">Student Code</p>
                    <div className="flex items-center gap-2">
                      <div className="bg-indigo-100 border-4 border-black px-4 py-2 font-mono text-lg font-black tracking-wider" style={{ color: '#1e1b4b' }}>
                        {student.student_code}
                      </div>
                      <BrutalButton
                        variant="indigo"
                        size="sm"
                        onClick={() => handleCopyPin(student.student_code, student.full_name)}
                        className="flex items-center gap-1"
                      >
                        {copiedPin === student.student_code ? (
                          <Check size={16} />
                        ) : (
                          <Copy size={16} />
                        )}
                      </BrutalButton>
                    </div>
                  </div>
                  
                  <div>
                    <p className="font-bold text-xs uppercase text-gray-600" data-testid="pin-label">PIN</p>
                    <div className="bg-yellow-50 border-4 border-black px-4 py-2 text-xs leading-snug" style={{ color: '#78350f' }}>
                      PIN is stored securely and shown to the parent only at signup.
                      If the parent has forgotten it, use <b>Reset PIN</b> below to issue a new one.
                    </div>
                  </div>
                  <div className="flex gap-2 mt-1">
                    <BrutalButton variant="amber" size="sm"
                      onClick={() => { setChangingPin(changingPin === student.id ? null : student.id); setPinForm({ current_pin: '', new_pin: '', confirm_pin: '' }); }}
                      className="text-xs" data-testid={`change-pin-btn-${student.id}`}>
                      {changingPin === student.id ? 'Cancel' : 'Change PIN'}
                    </BrutalButton>
                  </div>
                  {changingPin === student.id && (
                    <div className="mt-2 p-3 bg-yellow-50 border-4 border-black space-y-2" data-testid={`change-pin-form-${student.id}`}>
                      <input type="password" placeholder="Current PIN" value={pinForm.current_pin}
                        onChange={(e) => setPinForm({ ...pinForm, current_pin: e.target.value })}
                        className="w-full border-2 border-black px-3 py-2 font-mono text-sm" />
                      <input type="password" placeholder="New PIN (4-10 digits)" value={pinForm.new_pin}
                        onChange={(e) => setPinForm({ ...pinForm, new_pin: e.target.value })}
                        className="w-full border-2 border-black px-3 py-2 font-mono text-sm" />
                      <input type="password" placeholder="Confirm New PIN" value={pinForm.confirm_pin}
                        onChange={(e) => setPinForm({ ...pinForm, confirm_pin: e.target.value })}
                        className="w-full border-2 border-black px-3 py-2 font-mono text-sm" />
                      <BrutalButton variant="indigo" size="sm" fullWidth
                        onClick={() => handleChangePin(student.id)}
                        disabled={changePinMutation.isPending}
                        data-testid={`submit-change-pin-${student.id}`}>
                        {changePinMutation.isPending ? 'Changing...' : 'Save New PIN'}
                      </BrutalButton>
                    </div>
                  )}
                </div>

              {/* Details */}
              <div className="space-y-2">
                  {student.age && (
                    <p className="font-medium">
                      <span className="font-bold">Age:</span> {student.age} years
                    </p>
                  )}
                  {student.grade_level && (
                    <p className="font-medium">
                      <span className="font-bold">Grade:</span> {student.grade_level}
                    </p>
                  )}
                  {user?.is_teacher && student.pending_parent_email && (
                    <p className="font-medium text-sm">
                      <span className="font-bold">Parent Email:</span> {student.pending_parent_email}
                    </p>
                  )}
                  {user?.is_teacher && student.enrollment_status && (
                    <p className="font-medium text-sm">
                      <span className="font-bold">Status:</span>{' '}
                      <span className={`inline-block px-2 py-0.5 text-xs font-bold border-2 border-black ${student.enrollment_status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                        {student.enrollment_status === 'pending_parent_signup' ? 'Pending Parent' : student.enrollment_status}
                      </span>
                    </p>
                  )}
                  {student.interests && student.interests.length > 0 && (
                    <div>
                      <p className="font-bold mb-1">Interests:</p>
                      <div className="flex flex-wrap gap-1">
                        {student.interests.map((interest, idx) => (
                          <BrutalBadge key={idx} variant="indigo" size="sm">
                            {interest}
                          </BrutalBadge>
                        ))}
                      </div>
                    </div>
                  )}
                  {student.virtues && student.virtues.length > 0 && (
                    <div>
                      <p className="font-bold mb-1">Learning:</p>
                      <div className="flex flex-wrap gap-1">
                        {student.virtues.map((virtue, idx) => (
                          <BrutalBadge key={idx} variant="emerald" size="sm">
                            ✨ {virtue}
                          </BrutalBadge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="border-t-4 border-black pt-4 mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-bold uppercase text-gray-600">Mastered</p>
                    <p className="text-2xl font-black">{student.mastered_tokens?.length || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase text-gray-600">Word Banks</p>
                    <p className="text-2xl font-black">{student.assigned_banks?.length || 0}</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <BrutalButton
                    variant="indigo"
                    size="sm"
                    fullWidth
                    onClick={() => handleEdit(student)}
                    className="flex items-center justify-center gap-1"
                  >
                    <Edit size={16} />
                    Edit
                  </BrutalButton>
                  <BrutalButton
                    variant="rose"
                    size="sm"
                    fullWidth
                    onClick={() => handleDelete(student)}
                    className="flex items-center justify-center gap-1"
                  >
                    <Trash2 size={16} />
                    Delete
                  </BrutalButton>
                </div>
                
                <BrutalButton
                  variant="emerald"
                  size="sm"
                  fullWidth
                  onClick={() => handleAssignBanks(student)}
                  className="flex items-center justify-center gap-1"
                >
                  <BookOpen size={16} />
                  Assign Word Banks
                </BrutalButton>

                <BrutalButton
                  variant="emerald"
                  size="sm"
                  fullWidth
                  onClick={() => handleOpenStoryDialog(student.id)}
                  className="flex items-center justify-center gap-1"
                  style={{ background: '#14b8a6', borderColor: '#0d9488' }}
                >
                  <Sparkles size={16} />
                  Generate Story
                </BrutalButton>

                <BrutalButton
                  variant="amber"
                  size="sm"
                  fullWidth
                  onClick={() => {
                    setResettingPin(student.id);
                    resetPinMutation.mutate(student.id);
                  }}
                  disabled={resettingPin === student.id}
                  className="flex items-center justify-center gap-1"
                  data-testid={`reset-pin-btn-${student.id}`}
                >
                  <RefreshCw size={16} className={resettingPin === student.id ? 'animate-spin' : ''} />
                  {resettingPin === student.id ? 'Resetting...' : 'Reset PIN'}
                </BrutalButton>

                {/* Spelling Controls */}
                <div className="flex gap-2">
                  <BrutalButton
                    variant={student.spellcheck_disabled ? 'rose' : 'emerald'}
                    size="sm"
                    fullWidth
                    onClick={() => spellcheckMutation.mutate(student.id)}
                    className="flex items-center justify-center gap-1 text-xs"
                    data-testid={`spellcheck-toggle-${student.id}`}
                  >
                    <SpellCheck size={14} />
                    {student.spellcheck_disabled ? 'Spellcheck OFF' : 'Spellcheck ON'}
                  </BrutalButton>
                  <BrutalButton
                    variant={student.spelling_mode === 'exact' ? 'rose' : 'emerald'}
                    size="sm"
                    fullWidth
                    onClick={() => spellingModeMutation.mutate(student.id)}
                    className="flex items-center justify-center gap-1 text-xs"
                    data-testid={`spelling-mode-${student.id}`}
                  >
                    <Type size={14} />
                    {student.spelling_mode === 'exact' ? 'Phonetic OFF' : 'Phonetic ON'}
                  </BrutalButton>
                  <BrutalButton
                    variant={student.ad_preferences?.allow_brand_stories ? 'emerald' : 'rose'}
                    size="sm"
                    fullWidth
                    onClick={() => {
                      const current = student.ad_preferences || { allow_brand_stories: false, preferred_categories: [], blocked_categories: [] };
                      adPrefMutation.mutate({
                        studentId: student.id,
                        prefs: { ...current, allow_brand_stories: !current.allow_brand_stories },
                      });
                    }}
                    className="flex items-center justify-center gap-1 text-xs"
                    data-testid={`brand-toggle-${student.id}`}
                  >
                    <Megaphone size={14} />
                    {student.ad_preferences?.allow_brand_stories ? 'Brand Stories ON' : 'Brand Stories OFF'}
                  </BrutalButton>
                </div>

                {/* Reading Goal */}
                <div className="border-4 border-black p-3 bg-violet-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Target size={16} className="text-violet-600" />
                      <span className="text-xs font-black uppercase text-gray-600">Reading Goal</span>
                    </div>
                    {student.reading_goal ? (
                      <div className="flex items-center gap-2">
                        <BrutalBadge variant="indigo" size="sm">
                          {student.reading_goal.stories_required} stories / {student.reading_goal.period}
                        </BrutalBadge>
                        <button onClick={() => removeGoalMutation.mutate(student.id)} className="text-xs text-red-500 font-bold hover:underline">Remove</button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic">No goal set</span>
                    )}
                  </div>
                  {goalForm.studentId === student.id ? (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] font-bold uppercase text-gray-500 block mb-0.5">Stories Required</label>
                          <select className="w-full px-2 py-1.5 border-2 border-black text-sm font-bold bg-white" value={goalForm.stories} onChange={e => setGoalForm({ ...goalForm, stories: parseInt(e.target.value) })}>
                            {[1,2,3,4,5,7,10,15,20].map(n => <option key={n} value={n}>{n} stories</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold uppercase text-gray-500 block mb-0.5">Period</label>
                          <select className="w-full px-2 py-1.5 border-2 border-black text-sm font-bold bg-white" value={goalForm.period} onChange={e => setGoalForm({ ...goalForm, period: e.target.value })}>
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="custom">Custom Range</option>
                          </select>
                        </div>
                      </div>
                      {goalForm.period === 'custom' && (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] font-bold uppercase text-gray-500 block mb-0.5">Start</label>
                            <input type="date" className="w-full px-2 py-1.5 border-2 border-black text-sm font-bold" value={goalForm.start} onChange={e => setGoalForm({ ...goalForm, start: e.target.value })} />
                          </div>
                          <div>
                            <label className="text-[10px] font-bold uppercase text-gray-500 block mb-0.5">End</label>
                            <input type="date" className="w-full px-2 py-1.5 border-2 border-black text-sm font-bold" value={goalForm.end} onChange={e => setGoalForm({ ...goalForm, end: e.target.value })} />
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <BrutalButton variant="emerald" size="sm" onClick={() => setGoalMutation.mutate({
                          id: student.id,
                          data: { stories_required: goalForm.stories, period: goalForm.period, custom_start: goalForm.start || undefined, custom_end: goalForm.end || undefined }
                        })}>Set Goal</BrutalButton>
                        <BrutalButton variant="default" size="sm" onClick={() => setGoalForm({ ...goalForm, studentId: null })}>Cancel</BrutalButton>
                      </div>
                    </div>
                  ) : (
                    <BrutalButton variant="indigo" size="sm" fullWidth onClick={() => setGoalForm({ studentId: student.id, stories: student.reading_goal?.stories_required || 2, period: student.reading_goal?.period || 'weekly', start: '', end: '' })} className="flex items-center justify-center gap-1">
                      <Target size={14} /> {student.reading_goal ? 'Change Goal' : 'Set Reading Goal'}
                    </BrutalButton>
                  )}
                </div>

                {/* Parental Controls */}
                <ParentalControlsPanel studentId={student.id} studentName={student.full_name} />
              </div>
            </BrutalCard>
          ))}
        </div>
      )}

      {/* Bulk Import Dialog */}
      {bulkImportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white border-4 border-black p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto shadow-[8px_8px_0_0_rgba(0,0,0,1)]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-black uppercase flex items-center gap-2">
                <FileSpreadsheet size={24} /> Bulk Import Students
              </h3>
              <button onClick={() => setBulkImportOpen(false)} className="p-1 hover:bg-gray-100 border-2 border-black">
                <X size={20} />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm font-medium mb-3">
                Upload a CSV file with columns: <code className="bg-gray-100 px-1 font-mono text-xs">full_name, age, grade_level, parent_email, interests</code>
              </p>
              <a
                href={studentAPI.getImportTemplateUrl()}
                download
                className="inline-flex items-center gap-2 text-sm font-bold text-indigo-700 hover:text-indigo-900 mb-4 border-b-2 border-indigo-300"
              >
                <Download size={16} /> Download CSV Template
              </a>
            </div>

            <div className="mb-4">
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setBulkImportFile(e.target.files?.[0] || null)}
                className="w-full border-4 border-black p-3 font-medium text-sm"
              />
            </div>

            {user?.is_teacher && (
              <div className="mb-4">
                <label className="block text-sm font-bold mb-1">Assign to Classroom (optional)</label>
                <select
                  value={bulkImportClassroom}
                  onChange={(e) => setBulkImportClassroom(e.target.value)}
                  className="w-full border-4 border-black p-3 font-medium text-sm bg-white"
                  style={{ color: '#111827' }}
                >
                  <option value="">-- No classroom (import only) --</option>
                  {(classroomsForImport || []).map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.grade_level || 'All grades'})</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">All imported students will be automatically added to this classroom</p>
              </div>
            )}

            {bulkImportResult && (
              <div className="mb-4 p-4 border-4 border-black bg-gray-50">
                <p className="font-black text-lg mb-2">Import Results</p>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="text-center p-2 bg-blue-100 border-2 border-black">
                    <p className="text-2xl font-black">{bulkImportResult.total_rows}</p>
                    <p className="text-xs font-bold uppercase">Total Rows</p>
                  </div>
                  <div className="text-center p-2 bg-emerald-100 border-2 border-black">
                    <p className="text-2xl font-black text-emerald-700">{bulkImportResult.created}</p>
                    <p className="text-xs font-bold uppercase">Created</p>
                  </div>
                  <div className="text-center p-2 bg-rose-100 border-2 border-black">
                    <p className="text-2xl font-black text-rose-700">{bulkImportResult.errors?.length || 0}</p>
                    <p className="text-xs font-bold uppercase">Errors</p>
                  </div>
                </div>
                {bulkImportResult.errors?.length > 0 && (
                  <div className="max-h-40 overflow-y-auto">
                    <p className="font-bold text-sm mb-1 text-rose-700">Errors:</p>
                    {bulkImportResult.errors.map((err, i) => (
                      <p key={i} className="text-xs font-medium text-rose-600">
                        Row {err.row}: {err.reason}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            <BrutalButton
              variant="indigo"
              size="lg"
              fullWidth
              onClick={handleBulkImport}
              disabled={bulkImportLoading || !bulkImportFile}
              className="flex items-center justify-center gap-2"
            >
              <Upload size={20} className={bulkImportLoading ? 'animate-spin' : ''} />
              {bulkImportLoading ? 'Importing...' : 'Upload & Import'}
            </BrutalButton>
          </div>
        </div>
      )}

      {/* Story Generation Dialog */}
      {storyDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white border-4 border-black p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto shadow-[8px_8px_0_0_rgba(0,0,0,1)]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-black uppercase">Generate Story</h3>
              <button onClick={() => setStoryDialogOpen(false)} className="p-1 hover:bg-gray-100 border-2 border-black">
                <X size={20} />
              </button>
            </div>

            {/* Student Selection */}
            <div className="mb-4">
              <p className="font-bold uppercase text-sm mb-2">Select Students</p>
              <label className="flex items-center gap-2 mb-2 cursor-pointer font-bold text-sm">
                <input
                  type="checkbox"
                  checked={selectedStudentIds.length === students.length}
                  onChange={handleSelectAll}
                  className="w-5 h-5 accent-indigo-600"
                />
                Select All
              </label>
              <div className="space-y-1 max-h-40 overflow-y-auto border-2 border-black p-2">
                {students.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 cursor-pointer p-1 hover:bg-indigo-50">
                    <input
                      type="checkbox"
                      checked={selectedStudentIds.includes(s.id)}
                      onChange={() => handleToggleStudent(s.id)}
                      className="w-5 h-5 accent-indigo-600"
                    />
                    <span className="font-bold">{s.full_name}</span>
                    {s.grade_level && <span className="text-xs text-gray-500">(Grade {s.grade_level})</span>}
                  </label>
                ))}
              </div>
            </div>

            {/* Story Prompt */}
            <div className="mb-4">
              <p className="font-bold uppercase text-sm mb-2">Story Prompt</p>
              <textarea
                value={storyPrompt}
                onChange={(e) => setStoryPrompt(e.target.value)}
                placeholder="Describe the story theme or idea..."
                className="w-full border-4 border-black p-3 font-medium text-sm min-h-[80px] resize-y"
              />
            </div>

            {/* Quick Ideas */}
            <div className="mb-4">
              <p className="font-bold uppercase text-xs mb-2 text-gray-600">Quick Ideas</p>
              <div className="flex flex-wrap gap-2">
                {quickIdeas.map((idea) => (
                  <button
                    key={idea}
                    onClick={() => setStoryPrompt(idea)}
                    className="text-xs font-bold px-3 py-1 border-2 border-black bg-indigo-50 hover:bg-indigo-100 transition-colors"
                  >
                    {idea}
                  </button>
                ))}
              </div>
            </div>

            {/* Personalization Toggle */}
            <div className="mb-4">
              <label className="flex items-center gap-3 cursor-pointer p-3 border-2 border-black bg-emerald-50 hover:bg-emerald-100 transition-colors">
                <input
                  type="checkbox"
                  checked={personalized}
                  onChange={(e) => setPersonalized(e.target.checked)}
                  className="w-5 h-5 accent-emerald-600"
                />
                <div>
                  <span className="font-bold text-sm">Personalize to child's profile</span>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {personalized
                      ? 'Story will use interests, strengths, weaknesses, virtues, belief system & cultural context'
                      : 'Story will be generic, using only the prompt and word banks'}
                  </p>
                </div>
              </label>
            </div>

            {/* Progress */}
            {storyGenerating && storyProgress && (
              <div className="mb-4 p-3 bg-indigo-50 border-2 border-indigo-300 font-bold text-sm text-indigo-800">
                {storyProgress}
              </div>
            )}

            {/* Generate Button */}
            <BrutalButton
              variant="indigo"
              size="lg"
              fullWidth
              onClick={handleGenerateStories}
              disabled={storyGenerating || selectedStudentIds.length === 0 || !storyPrompt.trim()}
              className="flex items-center justify-center gap-2"
            >
              <Sparkles size={20} className={storyGenerating ? 'animate-spin' : ''} />
              {storyGenerating
                ? storyProgress || 'Generating...'
                : `Generate Story for ${selectedStudentIds.length} Student${selectedStudentIds.length !== 1 ? 's' : ''}`}
            </BrutalButton>
          </div>
        </div>
      )}

      {/* Student Form Dialog */}
      <StudentFormDialog
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingStudent(null);
          setAssigningBanksStudent(null);
        }}
        student={editingStudent}
        guardianId={user?.id}
        focusOnBanks={!!assigningBanksStudent}
        classrooms={classroomsForImport || []}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={!!deletingStudent}
        onClose={() => setDeletingStudent(null)}
        onConfirm={() => deleteMutation.mutate(deletingStudent.id)}
        title="Delete Student"
        message={`Are you sure you want to delete ${deletingStudent?.full_name}? This action cannot be undone.`}
        isLoading={deleteMutation.isPending}
      />

      {/* Floating Bulk Action Bar */}
      {bulkSelectedIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-white border-4 border-black px-6 py-3 shadow-[6px_6px_0_0_rgba(0,0,0,1)]">
          <span className="font-black text-sm">{bulkSelectedIds.length} selected</span>
          <BrutalButton
            variant="rose"
            size="sm"
            onClick={() => setBulkDeleteConfirmOpen(true)}
            className="flex items-center gap-1"
          >
            <Trash2 size={16} />
            Delete Selected ({bulkSelectedIds.length})
          </BrutalButton>
          <BrutalButton
            variant="amber"
            size="sm"
            onClick={() => setBulkSelectedIds([])}
          >
            Deselect All
          </BrutalButton>
        </div>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      {bulkDeleteConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white border-4 border-black p-6 max-w-md w-full mx-4 shadow-[8px_8px_0_0_rgba(0,0,0,1)]">
            <h3 className="text-2xl font-black uppercase mb-4 text-rose-700">Confirm Bulk Delete</h3>
            <p className="font-bold text-lg mb-6">
              Delete {bulkSelectedIds.length} student{bulkSelectedIds.length !== 1 ? 's' : ''} and all their data? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <BrutalButton
                variant="rose"
                size="lg"
                fullWidth
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="flex items-center justify-center gap-2"
              >
                <Trash2 size={20} className={bulkDeleting ? 'animate-spin' : ''} />
                {bulkDeleting ? 'Deleting...' : `Delete ${bulkSelectedIds.length} Student${bulkSelectedIds.length !== 1 ? 's' : ''}`}
              </BrutalButton>
              <BrutalButton
                variant="amber"
                size="lg"
                fullWidth
                onClick={() => setBulkDeleteConfirmOpen(false)}
                disabled={bulkDeleting}
              >
                Cancel
              </BrutalButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentsTab;
